import React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import PartnerTableBar from 'modules/partner/components/PartnerTableBar';
import VehiclesTable from '../VehiclesTable';
import { PartnerVehicleModel } from 'logic/partner/logic/partner-vehicles';
import VehiclesDetail from '../VehiclesDetail';
import VehiclesCreate from '../VehiclesCreate';
import Confirm, { MessageType } from 'common/components/Confirm';
import { PartnerCompanySelectModel } from 'logic/partner/logic/partner-partners';
import PartnerPairing from 'modules/partner/components/PartnerPairing';
import { PairingItemType } from 'logic/partner/partner';
import { ReadOnlyMonitoredObjectType } from 'generated/new-main';
import { PaginatedResponse, PaginationParams } from 'common/model/pagination';
import { LayoutContent } from 'common/components/Layout/Content';
import { PartnerPairingKey, PartnerPairingModel } from 'common/model/partner-pairing';
import TableBar from 'common/components/TableBar';
import { Button } from 'antd';
import Collapse from 'common/components/Collapse';
import { LayoutSidePanel } from 'common/components/Layout/SidePanel';
import VehiclesDetailCard from '../VehiclesDetailCard';
import VehiclesLinkedItems from './VehiclesLinkedItems';
import VehiclesActions from '../VehiclesActions';

interface Props extends WithTranslation {
    edit: boolean;
    loading: boolean;
    data?: PaginatedResponse<PartnerVehicleModel[]>;
    selected?: PartnerVehicleModel;
    create?: PartnerVehicleModel;
    delete?: PartnerVehicleModel;
    search?: { text: string };
    pairing?: PartnerPairingModel;
    companies?: PartnerCompanySelectModel[];
    clients?: PartnerCompanySelectModel[];
    selectedCompany?: PartnerCompanySelectModel;
    vehicleTypes?: ReadOnlyMonitoredObjectType[];
    pairingLoading?: boolean;
    unpairingLoading?: boolean;
    createLoading?: boolean;
    editLoading?: boolean;
    removeLoading?: boolean;
    onBarCompanyFilterChange?: (id: string) => void;
    onBarCompanyFilterCancel?: () => void;
    onBarCompanyFilterSearch?: (value: string) => void;
    onBarActionsCreate?: () => void;
    onTableRowSelect?: (id: string) => void;
    onDetailFormCancel?: () => void;
    onDetailFormSubmit?: (model: PartnerVehicleModel) => void;
    onDetailActionsUnpair?: (linked: PairingItemType) => void;
    onDeleteConfirm?: () => void;
    onDeleteCancel?: () => void;
    onCreateSubmit?: (model: PartnerVehicleModel) => void;
    onCreateCancel?: () => void;
    onPairingKeySelect?: (key: PartnerPairingKey) => void;
    onPairingItemSelect?: (itemId: string) => void;
    onPairingSearch?: (text: string) => void;
    onPairingConfirm?: () => void;
    onPairingCancel?: () => void;
    onPaginationChange?: (pagination: PaginationParams) => void;
    onActionsDelete?: () => void;
    onActionsPair?: () => void;
    onActionsUpdate?: () => void;
    onClientsFilterSearch?: (text: string) => void;
}

function Vehicles(props: Props) {
    const { t } = props;

    return (
        <>
            {props.pairing && (
                <PartnerPairing
                    keys={props.pairing.keys}
                    keySelected={props.pairing.keySelected}
                    items={props.pairing.items}
                    itemIdSelected={props.pairing.itemIdSelected}
                    search={props.pairing.search}
                    loading={props.pairing.loading}
                    keySource={PartnerPairingKey.vehicles}
                    pairingLoading={props.pairingLoading}
                    onKeySelect={props.onPairingKeySelect}
                    onItemSelect={props.onPairingItemSelect}
                    onSearch={props.onPairingSearch}
                    onConfirm={props.onPairingConfirm}
                    onCancel={props.onPairingCancel}
                />
            )}

            {props.create && (
                <VehiclesCreate
                    createLoading={props.createLoading}
                    model={props.create}
                    vehicleTypes={props.vehicleTypes}
                    clients={props.clients}
                    selectedCompany={props.selectedCompany}
                    onClientSearch={props.onClientsFilterSearch}
                    onSubmit={props.onCreateSubmit}
                    onCancel={props.onCreateCancel}
                />
            )}

            {props.delete && (
                <Confirm
                    danger
                    loading={props.removeLoading}
                    header={t('PartnerVehicles.deleteHeader')}
                    message={`${t('PartnerVehicles.deleteConfirm')} ${props.delete?.licensePlate}`}
                    type={MessageType.WARNING}
                    confirmLabel={props.t('common.delete')}
                    onConfirm={props.onDeleteConfirm}
                    onCancel={props.onDeleteCancel}
                />
            )}

            <LayoutContent
                className="partner-vehicles no-padding"
                mainSizes={{ xs: 24, sm: 24, md: 18 }}
                extraSizes={[{ xs: 24, sm: 24, md: 6 }]}
                main={
                    <>
                        <TableBar
                            heading={t('common.vehicles')}
                            filters={[
                                <PartnerTableBar
                                    companies={props.companies}
                                    selectedCompany={props.selectedCompany}
                                    onCompanyFilterChange={props.onBarCompanyFilterChange}
                                    onCompanyFilterCancel={props.onBarCompanyFilterCancel}
                                    onCompanyFilterSearch={props.onBarCompanyFilterSearch}
                                />
                            ]}
                            actions={[
                                <Button size="large" type="dashed" onClick={props.onBarActionsCreate}>
                                    {t('PartnerVehicles.addNewVehicle')}
                                </Button>
                            ]}
                        />
                        <VehiclesTable
                            loading={props.loading}
                            data={props.data}
                            vehicleTypes={props.vehicleTypes}
                            selected={props.selected}
                            onTableRowSelect={props.onTableRowSelect}
                            onPaginationChange={props.onPaginationChange}
                        />
                    </>
                }
                extra={[
                    props.selected ? (
                        <LayoutSidePanel
                            header={props.selected.licensePlate}
                            body={
                                <Collapse expandIconPosition="right" bordered={true} defaultActiveKey={['1', '2']}>
                                    <Collapse.Panel header={props.t('common.detail')} key="1">
                                        {props.edit ? (
                                            <VehiclesDetail
                                                editLoading={props.editLoading}
                                                model={props.selected}
                                                vehicleTypes={props.vehicleTypes}
                                                onFormCancel={props.onDetailFormCancel}
                                                onFormSubmit={props.onDetailFormSubmit}
                                            />
                                        ) : (
                                            <VehiclesDetailCard
                                                data={props.selected}
                                                vehicleTypes={props.vehicleTypes}
                                            />
                                        )}
                                    </Collapse.Panel>
                                    {!props.edit ? (
                                        <Collapse.Panel header={props.t('Partner.linkedItems')} key="2">
                                            <VehiclesLinkedItems
                                                unpairLoading={props.unpairingLoading}
                                                model={props.selected}
                                                onUnpair={props.onDetailActionsUnpair}
                                            />
                                        </Collapse.Panel>
                                    ) : (
                                        <></>
                                    )}
                                </Collapse>
                            }
                            footer={
                                !props.edit && (
                                    <VehiclesActions
                                        editLoading={props.editLoading}
                                        onDelete={props.onActionsDelete}
                                        onPair={props.onActionsPair}
                                        onUpdate={props.onActionsUpdate}
                                    />
                                )
                            }
                        />
                    ) : null
                ]}
            />
        </>
    );
}

export default withTranslation()(Vehicles);
